import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaProjectDiagram,
  FaChartLine,
  FaFutbol,
  FaRobot,
  FaCrown,
  FaArrowRight,
  FaCheckCircle,
  FaChartBar,
  FaFire,
  FaEnvelope,
  FaBrain,
  FaDatabase,
  FaVial,
  FaStar,
  FaChartPie,
  FaHistory,
  FaMoneyBillWave,
  FaTag,
  FaGem,
  FaUserCircle,
  FaShieldAlt,
  FaCreditCard,
} from "react-icons/fa";
import Footer from "../components/Footer";

export default function LandingPage() {
  const navigate = useNavigate();

  const handleGetStarted = () => navigate("/register");
  const handleLogin = () => navigate("/login");
  const handleContact = () => navigate("/contact");
  const handleTermsOfUse = () => navigate("/terms-of-use");
  const handlePrivacy = () => navigate("/privacy");
  const handleScrollToFeatures = () => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  };
  const handleScrollToPricing = () => {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 relative pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/90 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-3 py-3 md:px-6">
          {/* ROW */}
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center shrink-0"
            >
              <img
                src="/VisionRoyale_logo.png"
                alt="Vision Royale"
                className="h-8 w-auto md:h-12 object-contain"
              />
            </button>

            {/* MOBILE CENTER BADGE (between logo & buttons) */}
            <div className="flex-1 flex justify-center lg:hidden px-2">
              <div className="inline-flex items-center gap-1 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2 py-1 text-[9px] font-semibold text-emerald-300 whitespace-nowrap">
                <span className="relative flex h-1.5 w-1.5 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
                <span>AI-Powered Value Edge Engine</span>
              </div>
            </div>

            {/* Desktop badge (center absolute only on lg+) */}
            <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2">
              <div className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-1.5 text-xs font-semibold text-emerald-300">
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                AI-Powered Value Edge Engine
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={handleLogin}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-lg border border-white/20 bg-purple-400/10 px-2 py-1.5 sm:px-3 md:px-5 md:py-2 text-[10px] sm:text-sm font-semibold text-purple-300 transition-all hover:scale-105 hover:shadow-lg hover:shadow-purple-600/30"
              >
                Sign In
              </button>

              <button
                onClick={handleGetStarted}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-lg border border-white/20 bg-green-400/10 px-2 py-1.5 sm:px-3 md:px-5 md:py-2 text-[10px] sm:text-sm font-semibold text-green-300 transition-all hover:scale-105 hover:shadow-lg hover:shadow-emerald-600/30"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section - NOW FULLY RESPONSIVE */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(34,197,94,0.15),transparent_50%),radial-gradient(ellipse_at_bottom_left,rgba(59,130,246,0.1),transparent_60%)]" />
          <div className="relative mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12 lg:py-20">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              {/* LEFT COLUMN - Text + Features List */}
              <div className="w-full lg:w-1/2">
                <div className="mb-4 flex flex-wrap justify-center gap-3 sm:gap-4">
                  <div className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-sm font-semibold text-emerald-300">
                    <FaStar className="text-xs shrink-0" />
                    <span>Data-Driven Intelligence</span>
                  </div>

                  <div className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-blue-400/30 bg-blue-400/10 px-4 py-1.5 text-sm font-semibold text-blue-300">
                    <FaStar className="text-xs shrink-0" />
                    <span>Strategic Edge</span>
                  </div>

                  <div className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-purple-400/30 bg-purple-400/10 px-4 py-1.5 text-sm font-semibold text-purple-300">
                    <FaStar className="text-xs shrink-0" />
                    <span>Where Vision Meets Value</span>
                  </div>
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
                  Detect{" "}
                  <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">
                    Mathematical Edge
                  </span>{" "}
                  Before the Market Does
                </h1>
                <p className="mt-4 md:mt-6 text-base leading-relaxed text-white/70 md:text-lg">
                  Vision Royale uses advanced AI and statistical models to
                  identify value events with positive expected value. Join
                  professional analysts who rely on data, not intuition.
                </p>
                <div className="mt-6 md:mt-8 flex flex-wrap justify-center gap-3 md:gap-4">
                  <button
                    onClick={handleGetStarted}
                    className="border border-white/20 group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-green-400/10 px-5 py-2.5 md:px-6 md:py-3 text-sm md:text-base font-semibold text-green-300 transition-all hover:scale-105 hover:shadow-lg hover:shadow-emerald-600/30"
                  >
                    Start 3-Day Free Trial
                  </button>

                  <button
                    onClick={handleScrollToFeatures}
                    className="border border-white/20 group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-blue-400/10 px-5 py-2.5 md:px-6 md:py-3 text-sm md:text-base font-semibold text-blue-300 transition-all hover:scale-105 hover:shadow-lg hover:shadow-blue-600/30"
                  >
                    Explore Features
                  </button>

                  <button
                    onClick={handleLogin}
                    className="border border-white/20 group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-purple-400/10 px-5 py-2.5 md:px-6 md:py-3 text-sm md:text-base font-semibold text-purple-300 transition-all hover:scale-105 hover:shadow-lg hover:shadow-purple-600/30"
                  >
                    Sign In
                  </button>
                </div>

                <p className="mt-4 md:mt-6 text-base leading-relaxed text-white/70 md:text-lg">
                  From daily curated selections to full match analytics and
                  AI-generated insights, Vision Royale transforms raw data into
                  strategic clarity.
                </p>

                {/* Trust Badges - Mobile friendly */}
                <div className="mt-6 md:mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
                  <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-2.5">
                    <FaRobot className="text-emerald-400 text-sm" />
                    <span className="text-xs md:text-sm text-white/70">
                      AI-Powered
                    </span>
                  </div>
                  <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-2.5">
                    <FaMoneyBillWave className="text-emerald-400 text-sm" />
                    <span className="text-xs md:text-sm text-white/70">
                      Positive EV
                    </span>
                  </div>
                  <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-2.5">
                    <FaDatabase className="text-emerald-400 text-sm" />
                    <span className="text-xs md:text-sm text-white/70">
                      Live Stats
                    </span>
                  </div>
                  <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-2.5">
                    <FaHistory className="text-emerald-400 text-sm" />
                    <span className="text-xs md:text-sm text-white/70">
                      Full History
                    </span>
                  </div>
                </div>

                <p className="mt-4 md:mt-6 text-base leading-relaxed text-white/70 md:text-lg">
                  Vision Royale is a data-driven football intelligence platform
                  that combines statistical modeling, AI analysis, and
                  structured performance tracking into a single strategic
                  environment.
                </p>

                {/* ADDED: Complete Features List - fills the empty space */}
                <div className="mt-8 md:mt-10 rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
                  <h3 className="text-base md:text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <FaCheckCircle className="text-emerald-400" />
                    Complete Analysis Platform
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      {
                        icon: <FaCalendarAlt />,
                        text: "Smart Calendar & Timeline",
                      },
                      {
                        icon: <FaProjectDiagram />,
                        text: "Unlimited Strategy Projects",
                      },
                      {
                        icon: <FaChartLine />,
                        text: "Performance Analytics & ROI",
                      },
                      { icon: <FaFutbol />, text: "Professional Match Center" },
                      { icon: <FaBrain />, text: "AI Predictive Models" },
                      { icon: <FaTag />, text: "Value Event Detection" },
                      { icon: <FaHistory />, text: "Transaction History" },
                      { icon: <FaUserCircle />, text: "Personal Dashboard" },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-sm text-white/70"
                      >
                        <span className="text-emerald-400 text-xs">
                          {item.icon}
                        </span>
                        <span>{item.text}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs px-2 py-1 rounded-full bg-emerald-400/10 text-emerald-300">
                        Free 3-Day trial
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-blue-400/10 text-blue-300">
                        Stripe Secure Payment
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-yellow-400/10 text-yellow-300">
                        Automatic Updates
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-purple-400/10 text-purple-300">
                        Daily Snapshots
                      </span>
                    </div>
                  </div>
                </div>
                <p className="mt-4 md:mt-6 text-base leading-relaxed text-white/70 md:text-lg">
                  Vision Royale is a next-generation football analytics platform
                  designed to help users identify statistical advantages across
                  daily matches.
                </p>

                <div className="mt-6 md:mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
                  <div className="flex items-center justify-center text-center rounded-xl border border-white/10 bg-white/5 p-2.5">
                    <span className="text-xs md:text-sm text-white/70">
                      Market Odds
                    </span>
                  </div>

                  <div className="flex items-center justify-center text-center rounded-xl border border-white/10 bg-white/5 p-2.5">
                    <span className="text-xs md:text-sm text-white/70">
                      Model Probability
                    </span>
                  </div>

                  <div className="flex items-center justify-center text-center rounded-xl border border-white/10 bg-white/5 p-2.5">
                    <span className="text-xs md:text-sm text-white/70">
                      Value Edge
                    </span>
                  </div>

                  <div className="flex items-center justify-center text-center rounded-xl border border-white/10 bg-white/5 p-2.5">
                    <span className="text-xs md:text-sm text-white/70">
                      Margin
                    </span>
                  </div>

                  <div className="flex items-center justify-center text-center rounded-xl border border-white/10 bg-white/5 p-2.5">
                    <span className="text-xs md:text-sm text-white/70">
                      Break-Even
                    </span>
                  </div>

                  <div className="flex items-center justify-center text-center rounded-xl border border-white/10 bg-white/5 p-2.5">
                    <span className="text-xs md:text-sm text-white/70">
                      Combined Odds
                    </span>
                  </div>

                  <div className="flex items-center justify-center text-center rounded-xl border border-white/10 bg-white/5 p-2.5">
                    <span className="text-xs md:text-sm text-white/70">
                      ROI
                    </span>
                  </div>

                  <div className="flex items-center justify-center text-center rounded-xl border border-white/10 bg-white/5 p-2.5">
                    <span className="text-xs md:text-sm text-white/70">
                      Events
                    </span>
                  </div>
                </div>
                <p className="mt-4 md:mt-6 text-base leading-relaxed text-white/70 md:text-lg">
                  Unlike traditional tip-based services, Vision Royale
                  identifies statistical inefficiencies — known as Value Edge —
                  and provides a visual overview of high-probability
                  opportunities mapped daily, empowering users to make informed,
                  data-driven decisions.
                </p>
              </div>

              {/* RIGHT COLUMN - Dashboard Cards (NOW VISIBLE ON MOBILE) */}
              <div className="w-full lg:w-1/2">
                <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-4 backdrop-blur">
                  <div className="rounded-xl border border-white/5 bg-slate-900 p-4">
                    {/* April Performance Snapshot */}
                    <div className="mb-6 rounded-xl bg-slate-800/60 p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <FaChartBar className="text-emerald-400" />
                        <span className="text-sm font-medium text-white">
                          April Performance Snapshot
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 md:gap-3 text-center">
                        <div className="rounded-lg bg-slate-900/80 p-2 md:p-3">
                          <div className="text-[10px] md:text-xs text-white/40">
                            Total
                          </div>
                          <div className="text-base md:text-xl font-bold text-white">
                            29
                          </div>
                        </div>
                        <div className="rounded-lg bg-slate-900/80 p-2 md:p-3">
                          <div className="text-[10px] md:text-xs text-white/40">
                            Won
                          </div>
                          <div className="text-base md:text-xl font-bold text-emerald-400">
                            8
                          </div>
                        </div>
                        <div className="rounded-lg bg-slate-900/80 p-2 md:p-3">
                          <div className="text-[10px] md:text-xs text-white/40">
                            Lost
                          </div>
                          <div className="text-base md:text-xl font-bold text-red-400">
                            21
                          </div>
                        </div>
                        <div className="rounded-lg bg-slate-900/80 p-2 md:p-3">
                          <div className="text-[10px] md:text-xs text-white/40">
                            ROI
                          </div>
                          <div className="text-base md:text-xl font-bold text-emerald-400">
                            +45.4%
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Daily Value Snapshot Header */}
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-sm md:text-base font-semibold text-white">
                          Daily Value Snapshot
                        </div>
                        <div className="text-xs text-white/45">
                          2026-4-28 • Live
                        </div>
                      </div>
                      <div className="rounded-full bg-emerald-400/10 px-3 py-1 text-xs text-emerald-400 border border-emerald-400/20">
                        <FaFire className="mr-1 inline text-xs" />
                        WON
                      </div>
                    </div>

                    {/* Market Overview */}
                    <div className="mb-4 rounded-xl bg-slate-800/60 p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <FaChartBar className="text-emerald-400" />
                        <span className="text-sm font-medium text-white">
                          Market Overview
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 md:gap-3 text-center">
                        <div className="rounded-lg bg-slate-900/80 p-2">
                          <div className="text-[9px] md:text-[10px] text-white/40">
                            Combined Odds
                          </div>
                          <div className="text-sm md:text-base font-bold text-white">
                            4.20
                          </div>
                        </div>
                        <div className="rounded-lg bg-slate-900/80 p-2">
                          <div className="text-[9px] md:text-[10px] text-white/40">
                            Model Prob
                          </div>
                          <div className="text-sm md:text-base font-bold text-emerald-400">
                            52.2%
                          </div>
                        </div>
                        <div className="rounded-lg bg-slate-900/80 p-2">
                          <div className="text-[9px] md:text-[10px] text-white/40">
                            ROI
                          </div>
                          <div className="text-sm md:text-base font-bold text-emerald-400">
                            320%
                          </div>
                        </div>
                        <div className="rounded-lg bg-slate-900/80 p-2">
                          <div className="text-[9px] md:text-[10px] text-white/40">
                            Events
                          </div>
                          <div className="text-sm md:text-base font-bold text-white">
                            3
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Included Events - Stacked vertically */}
                    <div>
                      <div className="space-y-3">
                        {[
                          {
                            country: "Saudi-Arabia",
                            league: "Pro League",
                            time: "18:00",
                            homeTeam: "Al Shabab",
                            homeScore: 1,
                            awayTeam: "Al-Fateh",
                            awayScore: 1,
                            eventType: "Goals Over/Under",
                            detail: "Under 3.5",
                            odds: "1.62",
                            prob: "82.00%",
                            edge: "20.3",
                            margin: "+20.3",
                            breakEven: "61.7",
                          },
                          {
                            country: "Saudi-Arabia",
                            league: "Pro League",
                            time: "18:45",
                            homeTeam: "NEOM",
                            homeScore: 1,
                            awayTeam: "Al-Hazm",
                            awayScore: 1,
                            eventType: "Goals Over/Under",
                            detail: "Under 3.5",
                            odds: "1.50",
                            prob: "86.00%",
                            edge: "19.3",
                            margin: "+19.3",
                            breakEven: "66.7",
                          },
                          {
                            country: "Saudi-Arabia",
                            league: "Pro League",
                            time: "20:00",
                            homeTeam: "Al Khaleej Saihat",
                            homeScore: 3,
                            awayTeam: "Al Najma",
                            awayScore: 1,
                            eventType: "Both Teams Score",
                            detail: "Yes",
                            odds: "1.73",
                            prob: "74.00%",
                            edge: "16.2",
                            margin: "+16.2",
                            breakEven: "57.8",
                          },
                        ].map((ev, idx) => (
                          <div
                            key={idx}
                            className="bg-gray-800 rounded-lg border border-gray-700 p-4 hover:border-green-500/50 transition-all"
                          >
                            {/* Header: Country, League, Time */}
                            <div className="grid grid-cols-3 items-center mb-3 text-green-400">
                              <div className="text-left">
                                <div className="text-sm font-semibold truncate pr-2 text-green-300/70">
                                  {ev.country}
                                </div>
                                <div className="text-[10px] font-bold truncate pr-2 text-green-400">
                                  {ev.league}
                                </div>
                              </div>

                              <div className="text-center">
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 rounded-full border border-green-500/30">
                                  <span className="text-[10px] font-bold text-green-400">
                                    FT
                                  </span>
                                </div>
                              </div>

                              <div className="text-right flex items-center justify-end gap-1">
                                <span>⏰</span>
                                <span className="text-xs text-green-300">
                                  {ev.time}
                                </span>
                              </div>
                            </div>

                            {/* Teams & Score */}
                            <div className="mb-3">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0 pr-2">
                                  <div className="text-sm font-semibold text-purple-300 truncate text-right">
                                    {ev.homeTeam}
                                  </div>
                                </div>

                                <div className="flex-shrink-0 flex items-center gap-2">
                                  <div className="font-bold text-lg text-white bg-gray-700/60 px-2 py-1 rounded">
                                    {ev.homeScore}
                                  </div>
                                  <span className="text-xs text-gray-400">
                                    -
                                  </span>
                                  <div className="font-bold text-lg text-white bg-gray-700/60 px-2 py-1 rounded">
                                    {ev.awayScore}
                                  </div>
                                </div>

                                <div className="flex-1 min-w-0 pl-2">
                                  <div className="text-sm font-semibold text-purple-300 truncate text-left">
                                    {ev.awayTeam}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Event Type & Market */}
                            <div className="mb-3">
                              <div className="text-center mb-1">
                                <div className="text-sm font-semibold text-green-300">
                                  {ev.eventType}
                                </div>
                                <div className="text-sm font-bold text-purple-400 mt-1">
                                  {ev.detail}
                                </div>
                              </div>
                            </div>

                            {/* Market Odds & Model Probability */}
                            <div className="grid grid-cols-2 gap-2 mb-3">
                              <div className="bg-gray-800/60 p-2 rounded text-center border border-gray-700/60">
                                <div className="text-xs text-gray-400">
                                  Market Odds
                                </div>
                                <div className="text-sm font-bold text-purple-400">
                                  {ev.odds}
                                </div>
                              </div>
                              <div className="bg-gray-800/60 p-2 rounded text-center border border-gray-700/60">
                                <div className="text-xs text-gray-400">
                                  Model Probability
                                </div>
                                <div className="text-sm font-bold text-purple-400">
                                  {ev.prob}
                                </div>
                              </div>
                            </div>

                            {/* VALUE EDGE */}
                            <div className="space-y-1 mb-3">
                              <div className="flex justify-between text-xs">
                                <span className="font-semibold text-gray-400">
                                  VALUE EDGE
                                </span>
                                <span className="font-bold text-green-400">
                                  +{ev.edge}
                                </span>
                              </div>
                              <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                                <div
                                  className="bg-gradient-to-r from-green-500 to-emerald-500 h-full transition-all duration-300"
                                  style={{
                                    width: `${Math.min(100, parseFloat(ev.edge) * 4)}%`,
                                  }}
                                />
                              </div>
                            </div>

                            {/* MARGIN & BREAK-EVEN */}
                            <div className="grid grid-cols-2 gap-2">
                              <div className="bg-gray-800/60 p-2 rounded text-center border border-gray-700/60">
                                <div className="text-xs text-gray-400">
                                  MARGIN
                                </div>
                                <div
                                  className={`text-sm font-bold ${ev.margin.includes("+") ? "text-green-400" : "text-red-400"}`}
                                >
                                  {ev.margin}%
                                </div>
                              </div>
                              <div className="bg-gray-800/60 p-2 rounded text-center border border-gray-700/60">
                                <div className="text-xs text-gray-400">
                                  BREAK-EVEN
                                </div>
                                <div className="text-sm font-bold text-purple-400">
                                  {ev.breakEven}%
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section
          id="features"
          className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-20"
        >
          <div className="mb-10 md:mb-12 text-center">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white">
              Built for the{" "}
              <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">
                Data-Driven Analyst
              </span>
            </h2>
            <p className="mx-auto mt-3 md:mt-4 max-w-2xl text-white/60 text-sm md:text-base">
              Every tool you need to discover, track, and analyze value
              opportunities.
            </p>
          </div>

          <div className="grid gap-5 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: <FaCalendarAlt />,
                title: "Smart Calendar",
                color: "emerald",
                desc: "Visualize performance over time. Click any day to see historical value events, outcomes, and ROI.",
                features: [
                  "Daily value snapshots",
                  "Historical tracking",
                  "Performance heatmaps",
                ],
              },
              {
                icon: <FaProjectDiagram />,
                title: "Strategy Projects",
                color: "blue",
                desc: "Create unlimited projects to test different betting strategies. Define step-by-step conditions.",
                features: [
                  "Unlimited strategies",
                  "Step-based tracking",
                  "Success/failure analytics",
                ],
              },
              {
                icon: <FaBrain />,
                title: "AI Predictive Models",
                color: "purple",
                desc: "Our AI analyzes millions of data points to calculate accurate win probabilities.",
                features: [
                  "Model probability",
                  "Value edge calculation",
                  "Break-even analysis",
                ],
              },
              {
                icon: <FaFutbol />,
                title: "Match Center",
                color: "amber",
                desc: "Access detailed match info: H2H history, standings, live odds, and advanced statistics.",
                features: [
                  "Head-to-head insights",
                  "League standings",
                  "Live odds comparison",
                  "Daily football match listings",
                ],
              },
              {
                icon: <FaTag />,
                title: "Premium Value Events",
                color: "rose",
                desc: "AI-powered detection of events with expected margin above break-even for Premium users.",
                features: [
                  "Top value picks",
                  "Advanced filters",
                  "Unlimited daily analysis",
                ],
              },
              {
                icon: <FaChartPie />,
                title: "Portfolio Analytics",
                color: "cyan",
                desc: "Track performance with professional-grade charts: ROI, Win/Loss, balance evolution.",
                features: [
                  "Real-time metrics",
                  "Custom date ranges",
                  "Export capabilities",
                ],
              },
            ].map((feature, idx) => (
              <div
                key={idx}
                className="group rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6 transition-all hover:scale-[1.02] hover:border-white/20 hover:bg-white/10"
              >
                <div className="mb-3 md:mb-4 flex items-center gap-3">
                  <div
                    className={`inline-flex rounded-xl bg-${feature.color}-400/10 p-2 md:p-3 text-${feature.color}-400 flex-shrink-0`}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-white">
                    {feature.title}
                  </h3>
                </div>
                <p className="mt-2 text-sm text-white/60">{feature.desc}</p>
                <div className="mt-3 md:mt-4 flex flex-wrap gap-1.5 md:gap-2">
                  {feature.features.map((feat, i) => (
                    <span
                      key={i}
                      className="text-xs px-2 py-0.5 md:py-1 rounded-full bg-white/5 border border-white/10 text-white/50"
                    >
                      {feat}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="mx-auto max-w-7xl">
          <div className="mb-10 md:mb-12 text-center">
            <p className="mx-auto mt-3 md:mt-4 max-w-2xl text-white/60 text-sm md:text-base">
              Vision Royale operates on a subscription model with scalable
              analytics infrastructure and tiered feature access.
            </p>
            <h2 className="mx-auto mt-2 text-2xl md:text-3xl lg:text-4xl font-bold text-white">
              Choose Your{" "}
              <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">
                Analytical Edge
              </span>
            </h2>
            <p className="mx-auto mt-3 md:mt-4 max-w-2xl text-white/60 text-sm md:text-base">
              Start free, upgrade as your analysis needs grow.
            </p>
          </div>

          <div className="grid gap-5 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {/* Free */}
            <div className="relative flex flex-col h-full rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6 transition-all hover:scale-[1.02]">
              <div className="absolute top-5 right-5 text-white/50 text-xs md:text-sm"></div>
              <div className="mb-3 md:mb-4 flex items-center gap-3">
                <div className="rounded-xl bg-gray-400/10 p-2 md:p-3 text-gray-400 w-fit flex-shrink-0">
                  <FaVial size={18} />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-white">
                  Free
                </h3>
              </div>
              <p className="mt-2 text-sm text-white/60">
                3-Day free trial of the Basic plan
              </p>
              <ul className="mt-4 space-y-2 text-sm text-white/60 flex-grow">
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-gray-400 text-xs" /> Calendar
                  access
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-gray-400 text-xs" /> Unlimited
                  projects
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-gray-400 text-xs" /> Today's
                  matches
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-gray-400 text-xs" /> Basic
                  statistics
                </li>
              </ul>
              <div className="mt-4 text-xs text-red-400/80 space-y-1">
                <p>✗ No Daily Value Snapshots</p>
                <p>✗ No AI Predictions</p>
                <p>✗ No Premium Analysis</p>
              </div>
              <button
                onClick={handleGetStarted}
                className="mt-6 w-full rounded-xl border border-white/20 py-2.5 font-medium text-white text-sm transition-all hover:bg-white/10"
              >
                Start Free
              </button>
            </div>

            {/* Basic */}
            <div className="relative flex flex-col h-full rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6 transition-all hover:scale-[1.02]">
              <div className="absolute top-5 right-5 text-white/50 text-xs md:text-sm">
                €4.99/month
              </div>
              <div className="mb-3 md:mb-4 flex items-center gap-3">
                <div className="rounded-xl bg-blue-400/10 p-2 md:p-3 text-blue-400 w-fit flex-shrink-0">
                  <FaDatabase size={18} />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-blue-400">
                  Basic
                </h3>
              </div>
              <p className="mt-2 text-sm text-white/60">
                Essential tracking and organizing
              </p>
              <ul className="mt-4 space-y-2 text-sm text-white/60 flex-grow">
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-blue-400 text-xs" /> Calendar
                  access
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-blue-400 text-xs" /> Unlimited
                  projects
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-blue-400 text-xs" /> Today's
                  matches
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-blue-400 text-xs" /> Basic
                  statistics
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-blue-400 text-xs" /> Standard
                  support
                </li>
              </ul>
              <div className="mt-4 text-xs text-red-400/80 space-y-1">
                <p>✗ No Daily Value Snapshots</p>
                <p>✗ No AI Predictions</p>
                <p>✗ No Premium Analysis</p>
              </div>
              <button
                onClick={handleGetStarted}
                className="mt-6 w-full rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 py-2.5 font-medium text-white text-sm transition-all hover:scale-105"
              >
                Choose Basic
              </button>
            </div>

            {/* Pro */}
            <div className="relative flex flex-col h-full rounded-2xl border border-white/10 bg-gradient-to-b from-emerald-400/5 to-transparent p-5 md:p-6 transition-all hover:scale-[1.02]">
              <div className="absolute top-5 right-5 text-white/50 text-xs md:text-sm">
                €13.99/month
              </div>
              <div className="mb-3 md:mb-4 flex items-center gap-3">
                <div className="rounded-xl bg-emerald-400/10 p-2 md:p-3 text-emerald-400 w-fit flex-shrink-0">
                  <FaGem size={18} />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-emerald-400">
                  Pro
                </h3>
              </div>
              <p className="mt-2 text-sm text-white/60">
                Professional AI analysis
              </p>
              <ul className="mt-4 space-y-2 text-sm text-white/60 flex-grow">
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-emerald-400 text-xs" />{" "}
                  Everything in Basic
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-emerald-400 text-xs" /> Daily
                  Value Snapshots
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-emerald-400 text-xs" /> AI
                  Predictions
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-emerald-400 text-xs" />{" "}
                  Advanced stats
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-emerald-400 text-xs" />{" "}
                  Priority support
                </li>
              </ul>
              <div className="mt-4 text-xs text-red-400/80 space-y-1">
                <p>✗ No Premium Analysis</p>
                <p>✗ Up to 50 daily AI matches</p>
              </div>
              <button
                onClick={handleGetStarted}
                className="mt-6 w-full rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 py-2.5 font-medium text-white text-sm transition-all hover:scale-105"
              >
                Go Pro
              </button>
            </div>

            {/* Premium - Most Popular */}
            <div className="relative flex flex-col h-full rounded-2xl border-2 border-purple-400/50 bg-gradient-to-b from-purple-400/10 to-transparent p-5 md:p-6 transition-all hover:scale-[1.02]">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-purple-400 px-3 py-0.5 text-xs font-bold text-slate-950 whitespace-nowrap">
                MOST POPULAR
              </div>
              <div className="absolute top-5 right-5 text-white/50 text-xs md:text-sm">
                €19.99/month
              </div>
              <div className="mb-3 md:mb-4 flex items-center gap-3">
                <div className="rounded-xl bg-purple-400/10 p-2 md:p-3 text-purple-400 w-fit flex-shrink-0">
                  <FaCrown size={18} />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-purple-400">
                  Premium
                </h3>
              </div>
              <p className="mt-2 text-sm text-white/60">
                Complete AI-powered analysis
              </p>
              <ul className="mt-4 space-y-2 text-sm text-white/60 flex-grow">
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-purple-400 text-xs" />{" "}
                  Everything in Pro
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-purple-400 text-xs" /> Premium
                  Analysis
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-purple-400 text-xs" /> Advanced
                  Premium AI
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-purple-400 text-xs" />{" "}
                  Unlimited daily AI matches
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-purple-400 text-xs" /> VIP
                  support
                </li>
              </ul>
              <button
                onClick={handleGetStarted}
                className="mt-6 w-full rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 py-2.5 font-medium text-white text-sm transition-all hover:scale-105"
              >
                Go Premium
              </button>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-20">
          <div className="rounded-2xl md:rounded-3xl bg-white/5  border border-white/10 p-6 md:p-10 lg:p-12 text-center">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white">
              Ready to Find Your{" "}
              <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">
                Value Edge
              </span>{" "}
              ?
            </h2>
            <p className="mx-auto mt-3 md:mt-4 max-w-2xl text-white/60 text-sm md:text-base">
              Join hundreds of analysts using mathematical models to make
              smarter decisions.
            </p>
            <div className="mt-6 md:mt-8 flex flex-wrap justify-center gap-3 md:gap-4">
              <button
                onClick={handleGetStarted}
                className="border border-white/20 group flex items-center gap-2 rounded-xl bg-green-400/10 text-green-300 px-5 py-2.5 md:px-6 md:py-3 font-semibold text-sm md:text-base transition-all hover:scale-105 hover:shadow-lg hover:shadow-emerald-600/30"
              >
                Start 3-Day Free Trial
              </button>
              <button
                onClick={handleContact}
                className="border border-white/20 group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-purple-400/10 px-5 py-2.5 md:px-6 md:py-3 text-sm md:text-base font-semibold text-purple-300 transition-all hover:scale-105 hover:shadow-lg hover:shadow-purple-600/30"
              >
                <FaEnvelope />
                Contact Us
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
